import { css } from '@emotion/core';
import { IconButton, OutlinedInput, Typography } from '@material-ui/core';
import { error, system500, system900 } from 'Mui/styles/color';
import SuccessButton from 'client/game_photoUpload/PhotoUpload/SuccessButton';
import CloseIcon from '@material-ui/icons/Close';
import React from 'react';

/**
 * @method ReferralCodeCard - 邀請碼卡片
 * @param {Object}   props
 * @param {Boolean}  props.isViewMode         - 是否純顯示，不可輸入 ( ex. 新客加入獎勵編輯設定數入邀請碼 )
 * @param {Boolean}  props.isLoading          - 是否顯示讀取中效果
 * @param {Boolean}  props.isShowImage        - 是否顯示圖片
 * @param {Boolean}  props.isShowCloseButton  - 是否顯示關閉按鈕
 * @param {String}   props.imageUrl           - 圖片 url
 * @param {String}   props.buttonText         - 按鈕文字
 * @param {String}   props.buttonTextColor    - 按鈕文字顏色
 * @param {String}   props.buttonBgColor      - 按鈕文字背景顏色
 * @param {String}   props.description        - 說明文案
 * @param {String}   props.themeTextColor     - 卡片文字顏色
 * @param {String}   props.themeBgColor       - 卡片背景顏色
 * @param {String}   props.title              - 卡片標題文字
 * @param {String}   props.placeholder        - 輸入框提醒文字
 * @param {String}   props.error              - 錯誤訊息文字
 * @param {Function} props.onClick            - 點擊事件
 * @param {Function} props.onClose            - 關閉事件
 * @returns {JSX.Element}
 */
export default function ReferralCodeCard({
    isViewMode = false,
    isLoading,
    isShowImage,
    isShowCloseButton,
    imageUrl,
    buttonText,
    buttonTextColor,
    buttonBgColor,
    description,
    themeTextColor,
    themeBgColor,
    title,
    error,
    placeholder,
    onClick = () => {},
    onClose = () => {},
    value,
    onChange = () => {}
}) {
    return (
        <div css={css(containerStyle(themeBgColor, Boolean(isShowImage)))}>
            {isShowCloseButton && (
                <div css={css(closeContinerStyle)}>
                    <IconButton aria-label="close" onClick={onClose} size="small">
                        <CloseIcon css={css(closeIconStyle)} />
                    </IconButton>
                </div>
            )}
            <Typography css={css(titleStyle(themeTextColor))} variant="h6">
                {title}
            </Typography>

            {isShowImage && (
                <div css={css(contentContainerStyle)}>
                    <img src={imageUrl} alt="referral code" css={css(imgStyle)} />
                </div>
            )}
            <div css={css([contentContainerStyle, inputStyle])}>
                <OutlinedInput
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    {...(isViewMode && { disabled: true })}
                    error={Boolean(error)}
                ></OutlinedInput>
            </div>
            <Typography variant="subtitle2" css={css([errorStyle])}>
                {error}
            </Typography>
            <div css={css([contentContainerStyle, buttonStyle])}>
                <SuccessButton
                    style={successButtonStyle}
                    text={buttonText}
                    componentStyle={{
                        buttonLabelColor: buttonTextColor,
                        buttonColor: buttonBgColor,
                        disableColor: buttonTextColor,
                        disableBgColor: buttonBgColor
                    }}
                    onClick={onClick}
                    isLoading={isLoading}
                    {...(isViewMode && { disabled: true })}
                />
            </div>
            {description && (
                <div css={css(discriptionContainerStyle)}>
                    <Typography variant="caption" css={css(discriptionStyle(themeTextColor))}>
                        {description}
                    </Typography>
                </div>
            )}
        </div>
    );
}

const successButtonStyle = css`
    min-height: 56px;
`;

const containerStyle = (themeBgColor, isShowImage) => css`
    width: 320px;
    min-height: ${isShowImage ? '438px' : '232px'};
    border-radius: 12px;
    background: ${themeBgColor};
    max-height: 600px;
    overflow: auto;
`;

const titleStyle = themeTextColor => css`
    text-align: center;
    color: ${themeTextColor};
    padding: 14px 24px;
`;

const errorStyle = css`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${error.color};
    padding-top: 8px;
`;

const contentContainerStyle = css`
    width: 100%;
    height:226px
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
`;

const imgStyle = css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 0 0;
`;

const inputStyle = css`
    padding-top: 16px;

    & .MuiOutlinedInput-root {
        background: #ffffff;
        border-radius: 8px;
        width: 100%;
        border: 1px solid ${system500.color};
        height: 48px;

        input {
            :placeholder {
                color: ${system500.color};
            }
            text-align: center;
        }
    }
`;

const buttonStyle = css`
    padding-bottom: 16px;
    padding-top: 8px;
`;

const discriptionContainerStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 0 24px 16px 24px;
`;

const discriptionStyle = color => css`
    text-align: center;
    color: ${color};
`;

const closeContinerStyle = css`
    position: absolute;
    right: 14px;
    top: 14px;
`;

const closeIconStyle = css`
    color: ${system900.color};
`;
